/**
 * ConversationExport Component
 *
 * Dialog for exporting conversation history in various formats.
 * Supports text and JSON formats.
 */

import React, { useState, useCallback } from 'react';
import { format } from 'date-fns';
import {
  Download,
  FileText,
  FileJson,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import type { Message, Conversation } from '@/types/messaging';

interface ConversationExportProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversation: Conversation | null;
  messages: Message[];
}

type ExportFormat = 'text' | 'json';

const isExportFormat = (value: string): value is ExportFormat => {
  return value === 'text' || value === 'json';
};

/**
 * Format messages as plain text
 */
const formatAsText = (conversation: Conversation, messages: Message[]): string => {
  const participant = conversation.participant;
  const participantName = participant
    ? (`${participant.firstName} ${participant.lastName}`.trim() || participant.username)
    : 'Unknown';

  const lines: string[] = [
    '═══════════════════════════════════════════════════════════',
    '                    CONVERSATION EXPORT                     ',
    '═══════════════════════════════════════════════════════════',
    '',
    `Conversation with: ${participantName}`,
    `Exported on: ${format(new Date(), 'MMMM d, yyyy h:mm a')}`,
    `Total messages: ${messages.length}`,
    '',
    '───────────────────────────────────────────────────────────',
    '',
  ];

  // Sort messages by date (oldest first for export)
  const sortedMessages = [...messages].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  let currentDate = '';

  sortedMessages.forEach((message) => {
    const messageDate = format(new Date(message.createdAt), 'MMMM d, yyyy');
    const messageTime = format(new Date(message.createdAt), 'h:mm a');

    // Add date separator if new day
    if (messageDate !== currentDate) {
      currentDate = messageDate;
      lines.push(`--- ${messageDate} ---`);
      lines.push('');
    }

    const senderName = `${message.sender.firstName} ${message.sender.lastName}`.trim() ||
      message.sender.username;

    // Handle deleted messages
    if (message.isDeleted) {
      lines.push(`[${messageTime}] ${senderName}: [Message deleted]`);
    } else {
      lines.push(`[${messageTime}] ${senderName}: ${message.content}`);
      if (message.editedAt) {
        lines.push(`        (edited ${format(new Date(message.editedAt), 'h:mm a')})`);
      }
    }

    // Add reply context if present
    if (message.replyTo) {
      const replyPreview = message.replyTo.content.substring(0, 50);
      lines.push(`        ↳ Reply to: "${replyPreview}${message.replyTo.content.length > 50 ? '...' : ''}"`);
    }

    lines.push('');
  });

  lines.push('───────────────────────────────────────────────────────────');
  lines.push('                     End of Export                         ');
  lines.push('═══════════════════════════════════════════════════════════');

  return lines.join('\n');
};

/**
 * Format messages as JSON
 */
const formatAsJson = (conversation: Conversation, messages: Message[]): string => {
  const participant = conversation.participant;
  const exportData = {
    exportedAt: new Date().toISOString(),
    conversation: {
      id: conversation.id,
      participant: participant
        ? {
            id: participant.id,
            name: `${participant.firstName} ${participant.lastName}`.trim() || participant.username,
            username: participant.username,
          }
        : null,
      createdAt: conversation.createdAt,
    },
    messageCount: messages.length,
    messages: messages
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
      .map((message) => ({
        id: message.id,
        sender: {
          id: message.sender.id,
          name: `${message.sender.firstName} ${message.sender.lastName}`.trim() ||
            message.sender.username,
        },
        content: message.isDeleted ? '[Message deleted]' : message.content,
        contentType: message.contentType,
        createdAt: message.createdAt,
        editedAt: message.editedAt,
        isDeleted: message.isDeleted,
        replyTo: message.replyTo
          ? {
              id: message.replyTo.id,
              content: message.replyTo.content,
            }
          : null,
      })),
  };

  return JSON.stringify(exportData, null, 2);
};

/**
 * Download text content as file
 */
const downloadFile = (content: string, filename: string, mimeType: string) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const ConversationExport: React.FC<ConversationExportProps> = ({
  open,
  onOpenChange,
  conversation,
  messages,
}) => {
  const [format, setFormat] = useState<ExportFormat>('text');
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = useCallback(async () => {
    if (!conversation) return;

    setIsExporting(true);

    try {
      const participant = conversation.participant;
      const participantName = participant
        ? (`${participant.firstName} ${participant.lastName}`.trim() || participant.username)
        : 'Unknown';
      const dateStr = new Date().toISOString().split('T')[0];

      let content: string;
      let filename: string;
      let mimeType: string;

      if (format === 'json') {
        content = formatAsJson(conversation, messages);
        filename = `conversation-${participantName.replace(/\s+/g, '_')}-${dateStr}.json`;
        mimeType = 'application/json';
      } else {
        content = formatAsText(conversation, messages);
        filename = `conversation-${participantName.replace(/\s+/g, '_')}-${dateStr}.txt`;
        mimeType = 'text/plain';
      }

      // Small delay to show loading state
      await new Promise((resolve) => setTimeout(resolve, 500));

      downloadFile(content, filename, mimeType);

      toast.success('Conversation exported successfully');
      onOpenChange(false);
    } catch (error) {
      console.error('[ConversationExport] Export error:', error);
      toast.error('Failed to export conversation');
    } finally {
      setIsExporting(false);
    }
  }, [conversation, messages, format, onOpenChange]);

  if (!conversation) return null;

  const participant = conversation.participant;
  const participantName = participant
    ? (`${participant.firstName} ${participant.lastName}`.trim() || participant.username)
    : 'Unknown';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" style={{ color: 'hsl(var(--primary))' }} />
            Export Conversation
          </DialogTitle>
          <DialogDescription>
            Export your conversation with {participantName} ({messages.length} messages)
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <Label className="text-sm font-medium mb-3 block">Export Format</Label>
          <RadioGroup
            value={format}
            onValueChange={(value) => {
              if (isExportFormat(value)) {
                setFormat(value);
              }
            }}
            className="grid gap-3"
          >
            <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer">
              <RadioGroupItem value="text" id="text" />
              <Label htmlFor="text" className="flex-1 cursor-pointer">
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Plain Text (.txt)</p>
                    <p className="text-xs text-muted-foreground">
                      Human-readable format, easy to read
                    </p>
                  </div>
                </div>
              </Label>
            </div>
            <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer">
              <RadioGroupItem value="json" id="json" />
              <Label htmlFor="json" className="flex-1 cursor-pointer">
                <div className="flex items-center gap-3">
                  <FileJson className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">JSON (.json)</p>
                    <p className="text-xs text-muted-foreground">
                      Structured data, includes metadata
                    </p>
                  </div>
                </div>
              </Label>
            </div>
          </RadioGroup>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isExporting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            disabled={isExporting}
            style={{
              backgroundColor: 'hsl(var(--primary))',
              color: 'hsl(var(--primary-foreground))',
            }}
          >
            {isExporting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Export
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConversationExport;
